import { useMutation } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { type SubmitEvent, useState } from 'react'

import type { CodeRequestedResponse } from '@/bindings/CodeRequestedResponse'
import type { ForgotPasswordRequest } from '@/bindings/ForgotPasswordRequest'
import type { LoginResponse } from '@/bindings/LoginResponse'
import type { PlatformForgotPasswordRequest } from '@/bindings/PlatformForgotPasswordRequest'
import type { PlatformResetPasswordRequest } from '@/bindings/PlatformResetPasswordRequest'
import type { ResetPasswordRequest } from '@/bindings/ResetPasswordRequest'
import { Button } from '@/components/button'
import { Field, Input, PasswordInput } from '@/components/field'
import { FormError } from '@/components/form-error'
import { Card } from '@/components/layout'
import { AuthPage } from '@/features/auth/auth-page'
import { codeRejected, useCodeStatus } from '@/features/auth/code-status'
import { CodeStatusPanel } from '@/features/auth/code-status-panel'
import { emptyIdentifierDraft, toLoginIdentifier } from '@/features/auth/identifier-draft'
import { IdentifierInput } from '@/features/auth/identifier-input'
import { PasswordChecklist } from '@/features/auth/password-checklist'
import { useSmsCoverage } from '@/features/auth/sms-coverage'
import { useAnonymousOnly } from '@/features/auth/use-anonymous-only'
import { ApiError, authPath, request } from '@/lib/api'
import { type System, type UserOf } from '@/lib/auth-store'
import { useLanguage } from '@/lib/i18n/store'
import { useT } from '@/lib/i18n/translate'
import {
  PASSWORD_MIN_RESTAURANT,
  PASSWORD_MIN_PLATFORM,
  passwordProblem,
  useCommonPasswords,
} from '@/lib/password'
import { afterLoginPath, forgotPasswordPathFor, loginPathFor } from '@/lib/return-path'
import { completeLogin } from '@/lib/session'

/**
 * Forgot password (C7): the same two-step shape as registration — request a
 * code, then identifier + code + new password redeem into a fresh login.
 * The response and the copy are uniform whether the identifier matched an
 * account (T11.5). Restaurant offers the Email | Phone toggle; platform is
 * email-only (T11.8).
 */
export function ForgotPasswordPage<S extends System>({ system }: { system: S }) {
  const t = useT()
  const search = useSearch({ from: forgotPasswordPathFor(system) })
  const navigate = useNavigate()
  const liveSessionLeave = useAnonymousOnly(system, search)
  const language = useLanguage()
  const [identifier, setIdentifier] = useState(() => emptyIdentifierDraft())
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [resent, setResent] = useState(false)
  const step = search.step ?? 'request'
  const platform = system === 'platform'
  const {
    status,
    record: recordStatus,
    clear: clearStatus,
  } = useCodeStatus('reset', platform ? `platform:${email.trim()}` : JSON.stringify(toLoginIdentifier(identifier)))
  const smsCovered = useSmsCoverage(!platform)

  const passwordMin = platform ? PASSWORD_MIN_PLATFORM : PASSWORD_MIN_RESTAURANT
  const commonPasswords = useCommonPasswords()
  const [showProblem, setShowProblem] = useState(false)
  const problem = passwordProblem(password, passwordMin, commonPasswords)

  // Also the resend: the answer is uniform, match or not; re-requesting
  // supersedes the code / leaves an open manual phone request alone (C7).
  const requestCode = useMutation({
    mutationFn: () => {
      const body: ForgotPasswordRequest | PlatformForgotPasswordRequest = platform
        ? { email: email.trim(), language }
        : { identifier: toLoginIdentifier(identifier), language }
      return request<CodeRequestedResponse>(authPath(system, '/forgot-password'), { method: 'POST', body })
    },
    onSuccess: recordStatus,
  })

  // All other sessions were revoked server-side; this one starts fresh.
  const reset = useMutation({
    mutationFn: () => {
      const body: ResetPasswordRequest | PlatformResetPasswordRequest = platform
        ? { email: email.trim(), code: code.trim(), new_password: password }
        : { identifier: toLoginIdentifier(identifier), code: code.trim(), new_password: password }
      return request<LoginResponse<UserOf<S>>>(authPath(system, '/reset-password'), { method: 'POST', body })
    },
    onSuccess: (response) => {
      clearStatus()
      completeLogin(system, response)
      return navigate({ to: afterLoginPath(search, system), replace: true })
    },
  })

  if (liveSessionLeave) return liveSessionLeave

  const toVerify = () => navigate({ to: forgotPasswordPathFor(system), search: { ...search, step: 'verify' } })

  function submitRequest(event: SubmitEvent) {
    event.preventDefault()
    requestCode.mutate(undefined, { onSuccess: () => void toVerify() })
  }

  function skipToVerify() {
    requestCode.reset()
    void toVerify()
  }

  function resend() {
    requestCode.mutate(undefined, { onSuccess: () => setResent(true) })
  }

  function switchToEmail() {
    requestCode.reset()
    setIdentifier({ ...identifier, channel: 'email' })
    void navigate({ to: forgotPasswordPathFor(system), search: { ...search, step: undefined } })
  }

  function submitReset(event: SubmitEvent) {
    event.preventDefault()
    if (problem) {
      setShowProblem(true)
      return
    }
    reset.mutate()
  }

  // Password rules are the backend's (C2); PASSWORD_* lands on its field, the rest form-level.
  const passwordError =
    reset.error instanceof ApiError && reset.error.code.startsWith('PASSWORD_')
      ? reset.error
      : showProblem && problem
        ? t(problem.key, problem.params)
        : undefined

  const identifierForm = platform ? (
    <Field label={t('auth.email')}>
      <Input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
    </Field>
  ) : (
    <IdentifierInput value={identifier} onChange={setIdentifier} smsHint={step === 'request'} />
  )

  return (
    <AuthPage>
      {step === 'request' ? (
        <Card className="flex flex-col gap-4 p-6">
          <h1 className="text-xl font-semibold">{t('auth.forgotPassword')}</h1>
          <p className="text-muted text-sm">{t(platform ? 'auth.forgotIntroEmail' : 'auth.forgotIntro')}</p>
          <form onSubmit={submitRequest} className="flex flex-col gap-4">
            {identifierForm}
            <FormError error={requestCode.error} />
            <Button type="submit" loading={requestCode.isPending} block>
              {t('auth.sendCode')}
            </Button>
          </form>
          <div className="flex flex-col items-center gap-2 text-sm">
            <button type="button" onClick={skipToVerify} className="text-accent-ink font-medium hover:underline">
              {t('auth.haveCode')}
            </button>
            <Link to={loginPathFor(system)} search={search} className="text-accent-ink font-medium hover:underline">
              {t('auth.login')}
            </Link>
          </div>
        </Card>
      ) : (
        <Card className="flex flex-col gap-4 p-6">
          <h1 className="text-xl font-semibold">{t('auth.resetTitle')}</h1>
          <form onSubmit={submitReset} className="flex flex-col gap-4">
            {identifierForm}
            <Field label={t('auth.code')}>
              <Input
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </Field>
            <Field
              label={t('auth.newPassword')}
              hint={<PasswordChecklist password={password} min={passwordMin} />}
              error={passwordError}
            >
              <PasswordInput
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            {resent && (
              <p role="status" className="text-muted text-sm">
                {t('auth.codeRequested')}
              </p>
            )}
            <FormError error={passwordError ? undefined : reset.error} />
            <FormError error={requestCode.error} />
            <Button type="submit" loading={reset.isPending} block>
              {t('auth.resetPassword')}
            </Button>
            <CodeStatusPanel
              status={status}
              channel={platform ? 'email' : identifier.channel}
              flow="reset"
              smsCovered={platform || smsCovered(identifier.nation)}
              codeRejected={codeRejected(reset.error)}
              onResend={resend}
              resendPending={requestCode.isPending}
              onUseEmail={platform ? undefined : switchToEmail}
            />
          </form>
        </Card>
      )}
    </AuthPage>
  )
}
